import {useEffect, useState, useCallback} from 'react';
import {Link} from 'react-router-dom';
import {Button, Spin, Table, Space, message} from 'antd';

import {PlacementTestsService} from './PlacementTestsService';
import {PlacementTestSearch} from './PlacementTestSearch';
import Title from 'antd/es/typography/Title';
import Text from 'antd/es/typography/Text';
import {useTablePagination} from '../../hooks/useTablePagination';

const placementTestsService = new PlacementTestsService();

export const PlacementTestsTable = () => {
  const [placementTests, setPlacementTests] = useState<Array<any> | []>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Search state management
  const [searchTerm, setSearchTerm] = useState<string>('');
  const {
    current,
    setCurrent,
    pageSize,
    total,
    setTotal,
    paginationConfig,
    paginationSelector,
  } = useTablePagination({itemName: 'placement tests'});

  const fetchPlacementTests = useCallback(
    async (page: number, limit: number, term?: string) => {
      setIsLoading(true);
      setIsError(false);

      try {
        const resp = term
          ? await placementTestsService.searchPlacementTestsByName(
              term,
              page,
              limit,
            )
          : await placementTestsService.getPlacementTests(page, limit);

        setPlacementTests(resp.items);
        setTotal(resp.total);
      } catch (error) {
        setIsError(true);
        console.log('Error fetching placement tests ->', error);
        message.error('Failed to load placement tests');
      } finally {
        setIsLoading(false);
      }
    },
    [setTotal],
  );

  useEffect(() => {
    fetchPlacementTests(current, pageSize, searchTerm || undefined);
  }, [current, pageSize, searchTerm, fetchPlacementTests]);

  // Handle search with new API
  const handleSearch = useCallback(
    async (term: string) => {
      if (term.length >= 3) {
        setSearchTerm(term);
        setCurrent(1);
      }
    },
    [setCurrent],
  );

  // Handle search clear
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setCurrent(1);
  }, [setCurrent]);

  const columns: any = [
    {
      title: 'ID',
      dataIndex: 'id',
      sorter: (a: any, b: any) => a.id.length - b.id.length,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (id: string) => <Link to={'/placement-tests/' + id}>{id}</Link>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a: any, b: any) => {
        return a?.name.localeCompare(b?.name);
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Description',
      dataIndex: 'description',
      sorter: (a: any, b: any) => {
        return (a?.description || '').localeCompare(b?.description || '');
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (description: string) => description || '-',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      sorter: (a: any, b: any) => {
        return (a?.type || '').localeCompare(b?.type || '');
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (type: string) => type || 'General',
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      sorter: (a: any, b: any) => {
        return (a?.duration || '').localeCompare(b?.duration || '');
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (duration: string) => duration || '-',
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      defaultSortOrder: 'descend',
      sorter: (a: any, b: any) => {
        return a?.createdAt.localeCompare(b?.createdAt);
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (createdAt: string) => new Date(createdAt).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'id',
      render: (id: string) => (
        <Space>
          <Link to={'/placement-tests/' + id}>
            <Button>View Details</Button>
          </Link>
          <Link to={'/placement-tests/' + id + '/edit'}>
            <Button type="default">Edit</Button>
          </Link>
          {/* <Popconfirm
            title="Are you sure you want to delete this placement test?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(id)}>
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm> */}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: 20,
        }}>
        <Title level={3} style={{margin: 0}}>
          Placement Tests
          {!isLoading && total > 0 && (
            <Text
              type="secondary"
              style={{fontSize: 14, fontWeight: 400, marginLeft: 8}}>
              ({total} total)
            </Text>
          )}
        </Title>
        <Space>
          {paginationSelector}
          <Button type="primary" size={'large'}>
            <Link to={'/placement-tests/create'} style={{color: 'inherit'}}>
              Create
            </Link>
          </Button>
        </Space>
      </div>
      <PlacementTestSearch
        onSearch={handleSearch}
        onClear={handleClearSearch}
        isLoading={isLoading && Boolean(searchTerm)}
      />
      {!isError && !isLoading && (
        <Table
          pagination={paginationConfig}
          columns={columns}
          dataSource={placementTests?.map(test => ({...test, key: test.id}))}
          rowKey={record => record.id}
          loading={isLoading}
        />
      )}
      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>Placement Tests fetching error ⚠️</Title>}
    </>
  );
};
